import { useEffect, useState, useMemo } from 'react';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { clearErrors, deleteUser, getAllUsers } from '../../actions/userAction';
import { DELETE_USER_RESET } from '../../constants/userConstants';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Search box + column visibility + density, all built into MUI's DataGrid
const CustomToolbar = () => (
    <GridToolbarContainer className="p-3 flex flex-wrap gap-2 items-center justify-between border-b">
        <div className="flex gap-1">
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </div>
        <GridToolbarQuickFilter placeholder="Search users..." />
    </GridToolbarContainer>
);

const UserTable = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { users, error } = useSelector((state) => state.users);
    const { loading, isDeleted, error: deleteError } = useSelector((state) => state.profile);

    const [pageSize, setPageSize] = useState(25);
    const [roleFilter, setRoleFilter] = useState("");
    const [genderFilter, setGenderFilter] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (deleteError) {
            enqueueSnackbar(deleteError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isDeleted) {
            enqueueSnackbar("User Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_USER_RESET });
        }
        dispatch(getAllUsers());
    }, [dispatch, error, deleteError, isDeleted, enqueueSnackbar]);

    const deleteUserHandler = (id, name) => {
        if (window.confirm(`Delete ${name}? This permanently removes their account and cannot be undone.`)) {
            dispatch(deleteUser(id));
        }
    }

    const allRows = useMemo(() => {
        const rows = [];
        users && users.forEach((item) => {
            rows.unshift({
                id: item._id,
                name: item.name,
                avatar: item.avatar?.url || "",
                email: item.email,
                gender: item.gender ? item.gender.toUpperCase() : "—",
                role: item.role,
                registeredOn: new Date(item.createdAt).toISOString().substring(0, 10),
                deleted: Boolean(item.deletedAt),
            });
        });
        return rows;
    }, [users]);

    const deletedCount = useMemo(() => allRows.filter((r) => r.deleted).length, [allRows]);

    const filteredRows = useMemo(() => {
        return allRows.filter((row) => {
            if (row.deleted !== showDeleted) return false;
            if (roleFilter && row.role !== roleFilter) return false;
            if (genderFilter && row.gender !== genderFilter) return false;
            return true;
        });
    }, [allRows, showDeleted, roleFilter, genderFilter]);

    const activeFilters = [
        roleFilter && { key: "role", label: `Role: ${roleFilter}`, clear: () => setRoleFilter("") },
        genderFilter && { key: "gender", label: `Gender: ${genderFilter}`, clear: () => setGenderFilter("") },
    ].filter(Boolean);

    const columns = [
        {
            field: "name",
            headerName: "Name",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return (
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0">
                            {params.row.avatar && (
                                <img draggable="false" src={params.row.avatar} alt={params.row.name} className="w-full h-full rounded-full object-cover" />
                            )}
                        </div>
                        {params.row.name}
                    </div>
                )
            },
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 220,
            flex: 0.7,
        },
        {
            field: "gender",
            headerName: "Gender",
            minWidth: 100,
            flex: 0.3,
        },
        {
            field: "role",
            headerName: "Role",
            minWidth: 100,
            flex: 0.3,
            renderCell: (params) => {
                return (
                    <>
                        {
                            params.row.role === "admin" ? (
                                <span className="text-xs bg-green-100 px-2 py-0.5 font-medium rounded-full text-green-800 capitalize">{params.row.role}</span>
                            ) : (
                                <span className="text-xs bg-purple-100 px-2 py-0.5 font-medium rounded-full text-purple-800 capitalize">{params.row.role}</span>
                            )
                        }
                    </>
                )
            },
        },
        {
            field: "registeredOn",
            headerName: "Registered On",
            type: "date",
            minWidth: 150,
            flex: 0.3,
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 150,
            flex: 0.4,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <Link
                        to={`/admin/user/${params.row.id}/orders`}
                        state={{ customerName: params.row.name }}
                        className="text-gray-600 hover:bg-gray-100 p-1.5 rounded-full"
                        title="View Order History"
                    >
                        <ReceiptLongIcon sx={{ fontSize: "20px" }} />
                    </Link>
                    {!params.row.deleted && (
                        <Link to={`/admin/user/${params.row.id}`} className="text-primary-blue hover:bg-blue-50 p-1.5 rounded-full" title="Edit"><EditIcon sx={{ fontSize: "20px" }} /></Link>
                    )}
                    {!params.row.deleted && (
                        <button onClick={() => deleteUserHandler(params.row.id, params.row.name)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full" title="Delete"><DeleteOutlineIcon sx={{ fontSize: "20px" }} /></button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <MetaData title="Admin Users | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">{showDeleted ? "Deleted Users" : "Users"}</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowDeleted((prev) => !prev)}
                        className="py-2 px-4 rounded shadow font-medium text-gray-700 border hover:bg-gray-50"
                    >
                        {showDeleted ? "← Back to Active Users" : `View Deleted Users (${deletedCount})`}
                    </button>
                    {!showDeleted && (
                        <Link to="/admin/user/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New User</Link>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-3 p-4 border-b">
                    <TextField label="Role" select size="small" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 150 }}>
                        <MenuItem value="">All Roles</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                    </TextField>
                    <TextField label="Gender" select size="small" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} sx={{ minWidth: 150 }}>
                        <MenuItem value="">All Genders</MenuItem>
                        <MenuItem value="MALE">Male</MenuItem>
                        <MenuItem value="FEMALE">Female</MenuItem>
                        <MenuItem value="—">Not Set</MenuItem>
                    </TextField>
                </div>

                {/* Active filter chips */}
                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-gray-50">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {activeFilters.map((f) => (
                            <Chip key={f.key} label={f.label} size="small" onDelete={f.clear} />
                        ))}
                        <button
                            onClick={() => { setRoleFilter(""); setGenderFilter(""); }}
                            className="text-xs text-primary-blue hover:underline ml-1"
                        >
                            Clear All
                        </button>
                    </div>
                )}

                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        rowHeight={64}
                        pageSize={pageSize}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        disableSelectIconOnClick
                        components={{ Toolbar: CustomToolbar }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default UserTable;